import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { formatCurrency } from '../../utils/formatters';

const TransactionModal = ({ visible, onCancel, transaction, defaultPortfolioId }) => {
  const { addTransaction, updateTransaction, goals, portfolios } = useData();

  const [formData, setFormData] = useState({
    type: 'contribution',
    portfolioId: defaultPortfolioId || '',
    amount: '',
    costBasis: '',
    date: dayjs().format('YYYY-MM-DD'),
    goalId: '',
    from: '',
    description: '',
  });

  // Close modal on Escape key
  useEscapeKey(onCancel, visible);

  const transactionTypes = [
    { id: 'contribution', name: 'Contribution' },
    { id: 'withdrawal', name: 'Withdrawal' },
    { id: 'dividend', name: 'Dividend' },
    { id: 'fee', name: 'Fee' },
    { id: 'manual', name: 'Manual Adjustment' },
  ];

  const goalOptions = [
    { id: '', name: 'None (Optional)' },
    ...goals.map(g => ({ id: g.id, name: `${g.emoji} ${g.name}` })),
  ];

  // FIX: Split into separate effects to avoid unnecessary updates
  useEffect(() => {
    if (transaction) {
      setFormData({
        type: transaction.type || 'contribution',
        portfolioId: transaction.portfolioId || '',
        amount: transaction.amount || '',
        costBasis: transaction.costBasis || '',
        date: transaction.date || dayjs().format('YYYY-MM-DD'),
        goalId: transaction.goalId || '',
        from: transaction.from || '',
        description: transaction.description || '',
      });
    }
  }, [transaction]);

  // Initialize form when modal opens fresh (no transaction)
  useEffect(() => {
    if (visible && !transaction) {
      setFormData({
        type: 'contribution',
        portfolioId: defaultPortfolioId || '',
        amount: '',
        costBasis: '',
        date: dayjs().format('YYYY-MM-DD'),
        goalId: '',
        from: '',
        description: '',
      });
    }
  }, [visible, defaultPortfolioId, transaction]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.type || !formData.portfolioId || !formData.amount || !formData.date || !formData.description) {
      return;
    }

    if (formData.type !== 'manual' && formData.amount <= 0) {
      return;
    }
    if (formData.amount === 0) {
      return;
    }

    const transactionData = {
      ...formData,
      amount: parseFloat(formData.amount),
      costBasis: formData.costBasis ? parseFloat(formData.costBasis) : null,
      goalId: formData.goalId || null,
      from: formData.from || null,
    };

    if (transaction) {
      updateTransaction(transaction.id, transactionData);
    } else {
      addTransaction(transactionData);
    }

    handleCancel();
  };

  const handleCancel = () => {
    setFormData({
      type: 'contribution',
      portfolioId: defaultPortfolioId || '',
      amount: '',
      costBasis: '',
      date: dayjs().format('YYYY-MM-DD'),
      goalId: '',
      from: '',
      description: '',
    });
    onCancel();
  };

  const selectedType = transactionTypes.find(t => t.id === formData.type) || transactionTypes[0];
  const selectedPortfolio = portfolios.find(p => p.id === formData.portfolioId);
  const selectedGoal = goalOptions.find(g => g.id === formData.goalId) || goalOptions[0];

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-2">
                  {transaction ? 'Edit Transaction' : 'Add Transaction'}
                </Dialog.Title>

                <hr className="mb-3 border-gray-200" />

                <form onSubmit={handleSubmit} className="space-y-2">
                  {/* Transaction Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Type <span className="text-red-600">*</span>
                    </label>
                    <Listbox value={selectedType} onChange={(value) => setFormData({ ...formData, type: value.id })}>
                      <div className="relative">
                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate">{selectedType.name}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {transactionTypes.map((type) => (
                              <Listbox.Option
                                key={type.id}
                                value={type}
                                className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={selected ? 'font-medium' : 'font-normal'}>{type.name}</span>
                                    {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  {/* Account */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account <span className="text-red-600">*</span>
                    </label>
                    <Listbox value={selectedPortfolio} onChange={(value) => setFormData({ ...formData, portfolioId: value.id })}>
                      <div className="relative">
                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate">
                            {selectedPortfolio ? `${selectedPortfolio.name} (${selectedPortfolio.type})` : 'Select Account'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {portfolios.map((portfolio) => (
                              <Listbox.Option
                                key={portfolio.id}
                                value={portfolio}
                                className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={selected ? 'font-medium' : 'font-normal'}>
                                      {portfolio.name} ({portfolio.type})
                                    </span>
                                    {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Amount */}
                    <div>
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (£) <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="amount"
                        type="number"
                        step="0.01"
                        min={formData.type === 'manual' ? undefined : '0.01'}
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* Date */}
                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                        Date <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Cost Basis (Optional) */}
                  <div>
                    <label htmlFor="costBasis" className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Basis (Optional)
                      <span className="ml-1 text-xs text-gray-500">
                        - For tracking purchase price of investments
                      </span>
                    </label>
                    <input
                      id="costBasis"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.costBasis}
                      onChange={(e) => setFormData({ ...formData, costBasis: e.target.value })}
                      placeholder="Enter cost basis if applicable"
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Assign to Goal */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign to Goal
                    </label>
                    <Listbox value={selectedGoal} onChange={(value) => setFormData({ ...formData, goalId: value.id })}>
                      <div className="relative">
                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate">{selectedGoal.name}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {goalOptions.map((goal) => (
                              <Listbox.Option
                                key={goal.id}
                                value={goal}
                                className={({ active }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'}`}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={selected ? 'font-medium' : 'font-normal'}>{goal.name}</span>
                                    {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-5 w-5" /></span>}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  {/* From */}
                  <div>
                    <label htmlFor="from" className="block text-sm font-medium text-gray-700 mb-1">
                      From (Optional)
                    </label>
                    <input
                      id="from"
                      type="text"
                      value={formData.from}
                      onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                      placeholder="e.g., Savings Account, Paycheck"
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white  rounded-lg  transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                    >
                      {transaction ? 'Update' : 'Add'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TransactionModal;
